/**
 * Ocean wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const oceanWallpapersPack: ToilePack = {
    slug: 'ocean-wallpapers',
    category: 'screens',
    name: 'Ocean wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'wave', label: 'Curling wave', subject: 'the inside of a turquoise wave curling over, light shining through the water and white foam along its lip'},
        {key: 'turtle', label: 'Sea turtle', subject: 'a sea turtle gliding through clear blue water with sunbeams falling from the surface and small fish below'},
        {key: 'deep', label: 'Deep sea', subject: 'a single glowing jellyfish drifting in the dark deep sea, its trailing tentacles lit pink and blue'},
    ],
    styles: [
        {key: 'clear', label: 'Clear water', style: 'underwater photograph look, crystal clarity, rippling caustic light, natural color', palette: 'turquoise, aqua and sunlit white', framing: 'Full-bleed, the subject in the lower two thirds'},
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolor painting, pigment bleeding in the wet paper, salt textures and soft edges', palette: 'cobalt, teal and pale sea-green on white paper', framing: 'Full-bleed, the subject in the lower half with washes above'},
        {key: 'abyss', label: 'Abyss', style: 'dark cinematic digital painting, bioluminescent glow, floating particles, heavy blue-black depth', palette: 'deep navy and black with glowing cyan and pink accents', framing: 'Full-bleed, the subject in the lower half, dark and quiet above'},
    ],
    directions: ['whale', 'coral reef', 'stormy sea'],
    directionHint: 'try: whale',
    seo: {
        title: 'Ocean Wallpapers: Curling Wave, Sea Turtle, Deep Sea',
        metaDescription: 'Ocean phone wallpapers: a turquoise wave curling, a sea turtle in sunbeams and a glowing jellyfish in the deep, in three styles.',
        h1: 'Ocean wallpapers: a curling wave, a sea turtle, a deep-sea jellyfish',
        tagline: 'Nine phone wallpapers from the surface to the deep, three scenes in three styles.',
        intro: 'Phone wallpapers of the sea: a turquoise wave curling from inside, a sea turtle gliding through sunbeams, and a glowing jellyfish in the deep dark. Each comes as an underwater photo look, a loose watercolour and a dark cinematic painting. People who love the sea can set them as they are. Type a different subject, such as a whale or a coral reef, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a tablet or laptop background',
            'a seaside home or dive shop print',
            'a calm desk background',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'A curling turquoise wave, a sea turtle in sunbeams, and a glowing jellyfish in the deep. Each scene is shown in three styles.'},
            {q: 'Can I change the subject?', a: 'Yes. Type a subject such as a whale, a coral reef or a stormy sea, and the styles carry it; a typed subject is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Clear water is an underwater photo look with rippling light. Watercolor has loose paint with pigment bleeding into wet paper. Abyss is a dark cinematic painting with cyan and pink glow.'},
            {q: 'Do the pictures have any text?', a: 'No. There is no text or lettering, and the upper part of each picture is kept calm for the clock.'},
        ],
    },
}
